import { type DragEvent, useCallback, useEffect, useRef, useState } from 'react';
import { bytes, when } from '../../lib/n4';
import { type Site, errText, post, siteApi, toMs } from './common';

type Root = 'project' | 'www' | 'data';
const ROOT_LABEL: Record<Root, string> = { project: 'Projekt', www: 'www (kiszolgált)', data: 'data (adat)' };
interface Entry { name: string; dir: boolean; size: number | null; mtime: number | null }

// A szerver-válasz tűrő olvasása: { entries | files | items: [{ name, dir | type, size, mtime | modified }] }, opcionálisan { roots }.
function readEntries(r: unknown): Entry[] {
  const o = (r ?? {}) as Record<string, unknown>;
  const list = (Array.isArray(r) ? r : o.entries ?? o.files ?? o.items ?? []) as Record<string, unknown>[];
  return list.map((e) => ({
    name: String(e.name ?? ''),
    dir: e.dir === true || e.is_dir === true || e.type === 'dir' || e.type === 'directory',
    size: typeof e.size === 'number' ? e.size : null,
    mtime: toMs(e.mtime ?? e.modified ?? e.mtime_ms ?? e.updated_at),
  })).filter((e) => e.name).sort((a, b) => Number(b.dir) - Number(a.dir) || a.name.localeCompare(b.name, 'hu'));
}
function readRoots(r: unknown): Root[] | null {
  const v = (r as { roots?: unknown } | null)?.roots;
  // { project: útvonal | null, www: …, data: … } — csak a létezők
  if (v && typeof v === 'object' && !Array.isArray(v)) return (['project', 'www', 'data'] as Root[]).filter((k) => (v as Record<string, unknown>)[k]);
  if (!Array.isArray(v)) return null;
  return v.map((x) => (typeof x === 'string' ? x : (x as { key?: string; name?: string; exists?: boolean }).exists === false ? '' : (x as { key?: string; name?: string }).key ?? (x as { name?: string }).name ?? ''))
    .filter((x): x is Root => x === 'project' || x === 'www' || x === 'data');
}
// Melyik gyökér értelmes a fajtához, amíg a szerver nem mondja meg (a nem létező gyökér 404-et ad, azt kiírjuk)
function guessRoots(s: Site): Root[] {
  const out: Root[] = [];
  if (s.project_id != null) out.push('project');
  if (s.kind === 'static' || s.static_root || (s.routes ?? []).some((r) => r.to === 'static')) out.push('www');
  if (s.kind === 'container' || (s.db_paths ?? []).length) out.push('data');
  return out.length ? out : ['www'];
}
const join = (dir: string, name: string) => (dir ? `${dir}/${name}` : name);
const fileIcon = (name: string) => (/\.(png|jpe?g|gif|webp|svg|avif|ico)$/i.test(name) ? '▣' : /\.(html?|css|js|mjs|ts|tsx|json|md)$/i.test(name) ? '‹›' : /\.(db|sqlite3?)$/i.test(name) ? '◫' : /\.(zip|tar|gz|br|7z)$/i.test(name) ? '▤' : '▢');

// Mappa fül: fájlkezelő a site gyökerein (project / www / data), feltöltés (gomb vagy ráhúzás), letöltés, törlés, új mappa, átnevezés.
export function FileManager({ site }: { site: Site }) {
  const [roots, setRoots] = useState<Root[]>(() => guessRoots(site));
  const [root, setRoot] = useState<Root>(() => guessRoots(site)[0] ?? 'www');
  const [path, setPath] = useState('');
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [uploading, setUploading] = useState<string | null>(null);
  const [drag, setDrag] = useState(false);
  const [mkdir, setMkdir] = useState<string | null>(null);
  const [rename, setRename] = useState<{ from: string; to: string } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const base = `/api/n4/sites/${site.id}/files`;

  const load = useCallback(() => {
    setErr('');
    siteApi<unknown>(site.id, `/files?root=${root}&path=${encodeURIComponent(path)}`)
      .then((r) => { setEntries(readEntries(r)); const rs = readRoots(r); if (rs && rs.length) setRoots(rs); })
      .catch((e) => { setEntries(null); setErr(errText(e, `A(z) ${ROOT_LABEL[root]} mappa`)); });
  }, [site.id, root, path]);
  useEffect(() => { load(); }, [load]);

  const flash = (t: string) => { setMsg(t); window.setTimeout(() => setMsg((m) => (m === t ? '' : m)), 4000); };
  const act = async (fn: () => Promise<unknown>, ok: string, what: string) => {
    try { await fn(); flash(ok); load(); } catch (e) { setErr(errText(e, what)); }
  };

  const upload = async (files: File[]) => {
    if (!files.length) return;
    setUploading(files.length === 1 ? files[0]!.name : `${files.length} fájl`); setErr('');
    try {
      const fd = new FormData(); fd.append('root', root); fd.append('path', path);
      for (const f of files) fd.append('file', f, f.name);
      const res = await fetch(`${base}/upload`, { method: 'POST', body: fd, credentials: 'same-origin' });
      let body: unknown = null; try { body = await res.clone().json(); } catch { /* nem JSON */ }
      if (res.ok && (body as { ok?: boolean } | null)?.ok === false) throw new Error((body as { error?: string }).error || 'nem sikerült');
      if (!res.ok) { let code = `${res.status}`; try { const j = (await res.json()) as { error?: string; message?: string }; code = j.message || j.error || code; } catch { /* nem JSON */ } throw new Error(res.status === 404 ? 'nem elérhető (404) — a szerver-oldal még készül' : `hiba (${code})`); }
      flash(`Feltöltve: ${files.length} fájl.`); load();
    } catch (e) { setErr(`Feltöltés: ${e instanceof Error ? e.message : 'nem sikerült'}`); } finally { setUploading(null); }
  };
  const onDrop = (e: DragEvent) => { e.preventDefault(); setDrag(false); void upload(Array.from(e.dataTransfer.files)); };
  const crumbs = path ? path.split('/') : [];

  return (
    <div className="a-site-fm">
      <div className="a-site-bar">
        <div className="a-site-seg" role="tablist">{roots.map((r) => (
          <button key={r} type="button" className={root === r ? 'on' : ''} onClick={() => { setRoot(r); setPath(''); setEntries(null); }}>{ROOT_LABEL[r]}</button>
        ))}</div>
        <span className="flex-1" />
        <input ref={fileInput} type="file" multiple hidden onChange={(e) => { void upload(Array.from(e.target.files ?? [])); e.target.value = ''; }} />
        <button className="a-btn sm pri" disabled={uploading != null} onClick={() => fileInput.current?.click()}>{uploading ? 'Feltöltés…' : '↑ Feltöltés'}</button>
        <button className="a-btn sm" onClick={() => setMkdir(mkdir == null ? '' : null)}>+ Új mappa</button>
        <button className="a-btn sm ghost" onClick={load} title="Frissítés">⟳</button>
      </div>
      <nav className="a-site-crumbs">
        <a onClick={() => setPath('')}>{ROOT_LABEL[root].split(' ')[0]}</a>
        {crumbs.map((c, i) => <span key={i}><i>/</i><a onClick={() => setPath(crumbs.slice(0, i + 1).join('/'))}>{c}</a></span>)}
      </nav>
      {mkdir != null && (
        <form className="a-site-inline" onSubmit={(e) => { e.preventDefault(); const n = mkdir.trim(); if (!n) return; setMkdir(null); void act(() => siteApi(site.id, '/files/mkdir', post({ root, path: join(path, n) })), `Mappa létrehozva: ${n}`, 'Új mappa'); }}>
          <input className="a-input" autoFocus placeholder="az új mappa neve" value={mkdir} onChange={(e) => setMkdir(e.target.value)} />
          <button type="submit" className="a-btn sm solid">Létrehozás</button><button type="button" className="a-btn sm ghost" onClick={() => setMkdir(null)}>Mégse</button>
        </form>
      )}
      {uploading && <div className="a-site-msg">Feltöltés: {uploading}… <span className="a-spin">⟳</span></div>}
      {msg && <div className="a-site-msg ok">{msg}</div>}
      {err && <div className="a-site-msg bad">{err}</div>}
      <div className={`a-site-tablewrap a-site-dropzone ${drag ? 'drag' : ''}`} onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setDrag(true); } }} onDragLeave={(e) => { if (e.currentTarget === e.target) setDrag(false); }} onDrop={onDrop}>
        <table className="a-site-table">
          <thead><tr><th className="ic" /><th>Név</th><th className="num">Méret</th><th className="tm">Módosítva</th><th className="acts" /></tr></thead>
          <tbody>
            {path && <tr className="dir" onClick={() => setPath(crumbs.slice(0, -1).join('/'))}><td className="ic">↰</td><td colSpan={4}>..</td></tr>}
            {entries?.map((e) => {
              const full = join(path, e.name);
              const isRen = rename?.from === full;
              return (
                <tr key={e.name} className={e.dir ? 'dir' : ''} onClick={() => { if (e.dir && !isRen) setPath(full); }}>
                  <td className="ic">{e.dir ? '▸' : fileIcon(e.name)}</td>
                  <td className="nm">{isRen ? (
                    <form className="a-site-inline" onClick={(ev) => ev.stopPropagation()} onSubmit={(ev) => { ev.preventDefault(); const to = rename.to.trim(); if (!to || to === e.name) { setRename(null); return; } setRename(null); void act(() => siteApi(site.id, '/files/rename', post({ root, from: full, to: join(path, to) })), `Átnevezve: ${to}`, 'Átnevezés'); }}>
                      <input className="a-input" autoFocus value={rename.to} onChange={(ev) => setRename({ from: full, to: ev.target.value })} onKeyDown={(ev) => { if (ev.key === 'Escape') setRename(null); }} />
                      <button type="submit" className="a-btn sm solid">OK</button>
                    </form>
                  ) : e.dir ? <span>{e.name}/</span> : <a href={`${base}/download?root=${root}&path=${encodeURIComponent(full)}`} onClick={(ev) => ev.stopPropagation()} download={e.name} title="Letöltés">{e.name}</a>}</td>
                  <td className="num">{e.dir ? '' : bytes(e.size ?? undefined)}</td>
                  <td className="tm">{when(e.mtime)}</td>
                  <td className="acts" onClick={(ev) => ev.stopPropagation()}>
                    {!e.dir && <a className="a-site-mini" href={`${base}/download?root=${root}&path=${encodeURIComponent(full)}`} download={e.name} title="Letöltés">↓</a>}
                    <button type="button" className="a-site-mini" title="Átnevezés" onClick={() => setRename({ from: full, to: e.name })}>✎</button>
                    <button type="button" className="a-site-mini danger" title="Törlés" onClick={() => { if (confirm(`Törlöd: ${full}${e.dir ? ' (a mappa teljes tartalmával)' : ''}?`)) void act(() => siteApi(site.id, `/files?root=${root}&path=${encodeURIComponent(full)}`, { method: 'DELETE' }), `Törölve: ${e.name}`, 'Törlés'); }}>×</button>
                  </td>
                </tr>
              );
            })}
            {entries && entries.length === 0 && <tr className="empty"><td colSpan={5}>Üres mappa — húzz ide fájlokat, vagy használd a Feltöltés gombot.</td></tr>}
            {!entries && !err && <tr className="empty"><td colSpan={5}>betöltés…</td></tr>}
          </tbody>
        </table>
        {drag && <div className="a-site-dropmsg">Engedd el: feltöltés ide → {ROOT_LABEL[root].split(' ')[0]}/{path}</div>}
      </div>
    </div>
  );
}
